import * as React from 'react';
import SwissLoyaltyCardContainer from 'containers/PoinzCards';
import Head from 'next/head';
import { DefaultPageProps } from 'types';
import { CreditCard } from 'model';
import { getCreditCardBySlug } from 'utils/contentful/poinzCards';
import { useAuth } from 'contexts/authUser/store';
import { useRouter } from 'next/router';
import { authUserSelectors } from 'contexts/authUser';
import { defaultGetStaticProps } from 'utils/staticPropsHelper';
import { getHeaderTitle } from 'utils/string';
import dynamic from 'next/dynamic';
import { useLoyalty } from 'contexts/loyalty';

type PoinzCardsProps = DefaultPageProps & {
  creditCard: CreditCard;
};

const LoadingDialog = dynamic(() => import('containers/PoinzCards/LoadingDialog/LoadingDialog'));

const BenefitsOverviewDialog = dynamic(
  () => import('containers/PoinzCards/BenefitsOverviewDialog')
);

const PoinzCards: React.FC<PoinzCardsProps> = ({ creditCard }) => {
  const { title, description, image } = creditCard.meta || {};
  const { isLoggedIn } = useAuth(state => ({
    isLoggedIn: authUserSelectors.isLoggedIn(state)
  }));
  const { userBalance, loadingUserBalance } = useLoyalty();
  const router = useRouter();

  return (
    <div className="PoinzCardsPage">
      <Head>
        <title>{getHeaderTitle(`${title} | Poinz Cards`)}</title>
        <meta name="description" key="desc" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {image && <meta property="og:image" content={image.url} />}
      </Head>
      <SwissLoyaltyCardContainer />
      <LoadingDialog
        open={router.query.state === 'success' && isLoggedIn}
        isLoading={loadingUserBalance}
        swissLoyaltyId={userBalance?.swissLoyaltyId}
        onClose={() => router.replace(router.pathname, undefined, { shallow: true })}
      />
      <BenefitsOverviewDialog />
    </div>
  );
};

export const getStaticProps = async (context: any) => {
  const { preview } = context;
  const { props } = await defaultGetStaticProps(context);

  let creditCard;
  try {
    creditCard = await getCreditCardBySlug('swiss-loyalty-cards', preview, props.lang);
    if (!creditCard) {
      return {
        notFound: true
      };
    }
  } catch (e) {
    console.error('Error fetching contentful data: ', e);
  }
  return {
    props: {
      ...props,
      creditCard: creditCard || null
    },
    revalidate: 10
  };
};

export default PoinzCards;
